import React from 'react'
import { api } from '../api/client'
import type { CardAccount } from '../api/client'

// The card account as the app last saw it, shared between the places that
// show it (Today, the plan card, the Card payments card): one of them moving
// the account on (starting the Stripe setup from the plan card) shows at once
// in the others, without each refetching.
let current: CardAccount | null = null
const listeners = new Set<(account: CardAccount | null) => void>()

// publishCardAccount is for whoever just fetched or changed the account.
export function publishCardAccount(account: CardAccount | null): void {
  current = account
  for (const listen of listeners) listen(account)
}

// subscribeCardAccount calls back on every publish; the return unsubscribes.
export function subscribeCardAccount(listen: (account: CardAccount | null) => void): () => void {
  listeners.add(listen)
  return () => {
    listeners.delete(listen)
  }
}

// scrollToCardPayments brings the Card payments section of Settings → Plan
// and payments into view, where the Stripe setup and its status live.
export function scrollToCardPayments(): void {
  document.getElementById('settings-section-card-payments')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
}

// useCardAccount fetches the card account once for the component's lifetime
// and follows what the rest of the app publishes about it afterwards. It is
// null until loaded and stays null if the request fails, which the
// visibility rules treat as "card payments not shown".
export function useCardAccount(): CardAccount | null {
  const [account, setAccount] = React.useState<CardAccount | null>(current)
  React.useEffect(() => {
    let live = true
    const unsubscribe = subscribeCardAccount((a) => {
      if (live) setAccount(a)
    })
    // Resolved first so a throw from the call is caught like a failed
    // request (a test's partial API stub, for one).
    Promise.resolve()
      .then(() => api.cardAccount())
      .then((a) => {
        if (live) publishCardAccount(a)
      })
      .catch(() => {
        // Card payments stay hidden; nothing else depends on this.
      })
    return () => {
      live = false
      unsubscribe()
    }
  }, [])
  return account
}
